import React, { useEffect } from 'react';
import './IronHeadAnimation.css';

const IronHeadAnimation = ({ playerRef, opponentRef, onComplete }) => {
  useEffect(() => {
    const elements = [];

    if (!playerRef.current || !opponentRef.current) {
      console.warn('IronHeadAnimation: Missing refs');
      onComplete();
      return;
    }

    const playerElement = playerRef.current;
    const opponentElement = opponentRef.current;

    // Get battle container for proper positioning
    const battleContainer = playerRef.current?.closest('.battle-screen') || 
                           playerRef.current?.closest('.battle-container') || 
                           document.body;

    // Get battle container rect for proper positioning
    const battleRect = battleContainer.getBoundingClientRect();
    const playerRect = playerElement.getBoundingClientRect();
    const opponentRect = opponentElement.getBoundingClientRect();
    
    // Calculate relative positions within battle container
    const playerX = playerRect.left - battleRect.left;
    const playerY = playerRect.top - battleRect.top;
    const opponentX = opponentRect.left - battleRect.left;
    const opponentY = opponentRect.top - battleRect.top;

    // Phase 1: Steel Head Hardening (0-600ms) - UNIQUE MECHANIC
    playerElement.classList.add('iron-head-steel-hardening');

    // Create metallic coating effect on attacker's head area
    const steelCoating = document.createElement('div');
    steelCoating.className = 'iron-head-steel-coating';
    steelCoating.style.position = 'absolute';
    steelCoating.style.left = `${playerX - 30}px`;
    steelCoating.style.top = `${playerY - 40}px`;
    steelCoating.style.width = '60px';
    steelCoating.style.height = '40px';
    steelCoating.style.zIndex = '1000';
    battleContainer.appendChild(steelCoating);
    elements.push(steelCoating);

    // Create steel hardening sparks
    for (let i = 0; i < 8; i++) {
      const spark = document.createElement('div');
      spark.className = 'iron-head-hardening-spark';
      spark.textContent = '✨';
      spark.style.position = 'absolute';
      spark.style.left = `${playerX + Math.cos(i * 45 * Math.PI / 180) * 25}px`;
      spark.style.top = `${playerY - 20 + Math.sin(i * 45 * Math.PI / 180) * 15}px`;
      spark.style.animationDelay = `${i * 75}ms`;
      spark.style.zIndex = '1001';
      battleContainer.appendChild(spark);
      elements.push(spark);
    }

    // Create metallic shine effect
    const metallicShine = document.createElement('div');
    metallicShine.className = 'iron-head-metallic-shine';
    metallicShine.style.position = 'absolute';
    metallicShine.style.left = `${playerX - 20}px`;
    metallicShine.style.top = `${playerY - 30}px`;
    metallicShine.style.width = '40px';
    metallicShine.style.height = '20px';
    metallicShine.style.zIndex = '1002';
    battleContainer.appendChild(metallicShine);
    elements.push(metallicShine);

    // Phase 2: Steel Ram Charge (600-1000ms) - UNIQUE CHARGING MECHANIC
    const phase2Timer = setTimeout(() => {
      // Create charging momentum lines
      for (let i = 0; i < 6; i++) {
        const momentumLine = document.createElement('div');
        momentumLine.className = 'iron-head-momentum-line';
        momentumLine.style.position = 'absolute';
        momentumLine.style.left = `${playerX - (i * 30)}px`;
        momentumLine.style.top = `${playerY + (i * 5)}px`;
        momentumLine.style.width = '20px';
        momentumLine.style.height = '3px';
        momentumLine.style.animationDelay = `${i * 50}ms`;
        momentumLine.style.zIndex = '1003';
        battleContainer.appendChild(momentumLine);
        elements.push(momentumLine);
      }

      // Create steel head projectile moving toward opponent
      const steelProjectile = document.createElement('div');
      steelProjectile.className = 'iron-head-steel-projectile';
      steelProjectile.style.position = 'absolute';
      steelProjectile.style.left = `${playerX}px`;
      steelProjectile.style.top = `${playerY}px`;
      steelProjectile.style.width = '40px';
      steelProjectile.style.height = '30px';
      steelProjectile.style.zIndex = '1004';
      
      // Animate projectile from player to opponent
      steelProjectile.style.setProperty('--start-x', `${playerX}px`);
      steelProjectile.style.setProperty('--start-y', `${playerY}px`);
      steelProjectile.style.setProperty('--end-x', `${opponentX}px`);
      steelProjectile.style.setProperty('--end-y', `${opponentY}px`);
      
      battleContainer.appendChild(steelProjectile);
      elements.push(steelProjectile);

      // Create steel ram aura around projectile
      const ramAura = document.createElement('div');
      ramAura.className = 'iron-head-ram-aura';
      ramAura.style.position = 'absolute';
      ramAura.style.left = `${playerX - 20}px`;
      ramAura.style.top = `${playerY - 20}px`;
      ramAura.style.width = '80px';
      ramAura.style.height = '70px';
      ramAura.style.zIndex = '1003';
      battleContainer.appendChild(ramAura);
      elements.push(ramAura);
    }, 600);
    elements.push({ type: 'timer', timer: phase2Timer });

    // Phase 3: Metallic Collision Impact (1000-1600ms) - UNIQUE IMPACT MECHANIC
    const phase3Timer = setTimeout(() => {
      opponentElement.classList.add('iron-head-metallic-impact');

      // Create metallic collision sparks (unique to steel moves)
      for (let i = 0; i < 12; i++) {
        const metallicSpark = document.createElement('div');
        metallicSpark.className = 'iron-head-metallic-spark';
        metallicSpark.textContent = '⚡';
        metallicSpark.style.position = 'absolute';
        metallicSpark.style.left = `${opponentX + Math.cos(i * 30 * Math.PI / 180) * 50}px`;
        metallicSpark.style.top = `${opponentY + Math.sin(i * 30 * Math.PI / 180) * 50}px`;
        metallicSpark.style.animationDelay = `${i * 30}ms`;
        metallicSpark.style.zIndex = '1005';
        battleContainer.appendChild(metallicSpark);
        elements.push(metallicSpark);
      }

      // Create steel collision shockwave
      const steelShockwave = document.createElement('div');
      steelShockwave.className = 'iron-head-steel-shockwave';
      steelShockwave.style.position = 'absolute';
      steelShockwave.style.left = `${opponentX - 80}px`;
      steelShockwave.style.top = `${opponentY - 80}px`;
      steelShockwave.style.width = '160px';
      steelShockwave.style.height = '160px';
      steelShockwave.style.zIndex = '1004';
      battleContainer.appendChild(steelShockwave);
      elements.push(steelShockwave);

      // Create small fragment sprites from Iron Head.png
      const smallFragments = [
        { x: 442, y: 0, width: 12, height: 12 },
        { x: 454, y: 0, width: 12, height: 12 },
        { x: 466, y: 0, width: 12, height: 12 },
        { x: 478, y: 0, width: 9, height: 8 },
        { x: 487, y: 0, width: 10, height: 10 }
      ];

      smallFragments.forEach((fragment, index) => {
        const fragmentSprite = document.createElement('div');
        fragmentSprite.className = 'iron-head-fragment';
        fragmentSprite.style.position = 'absolute';
        fragmentSprite.style.left = `${opponentX + Math.cos(index * 72 * Math.PI / 180) * 80}px`;
        fragmentSprite.style.top = `${opponentY + Math.sin(index * 72 * Math.PI / 180) * 80}px`;
        fragmentSprite.style.width = `${fragment.width}px`;
        fragmentSprite.style.height = `${fragment.height}px`;
        fragmentSprite.style.backgroundImage = `url('./Moves/Iron Head.png')`;
        fragmentSprite.style.backgroundPosition = `-${fragment.x}px -${fragment.y}px`;
        fragmentSprite.style.backgroundSize = '768px 576px';
        fragmentSprite.style.animationDelay = `${index * 30}ms`;
        fragmentSprite.style.zIndex = '1006';
        battleContainer.appendChild(fragmentSprite);
        elements.push(fragmentSprite);
      });

      // Create steel shockwave rings
      for (let i = 0; i < 4; i++) {
        const shockwave = document.createElement('div');
        shockwave.className = 'iron-head-shockwave';
        shockwave.style.position = 'absolute';
        shockwave.style.left = `${opponentX - 60 - (i * 25)}px`;
        shockwave.style.top = `${opponentY - 60 - (i * 25)}px`;
        shockwave.style.width = `${120 + (i * 50)}px`;
        shockwave.style.height = `${120 + (i * 50)}px`;
        shockwave.style.animationDelay = `${i * 80}ms`;
        shockwave.style.zIndex = '1004';
        battleContainer.appendChild(shockwave);
        elements.push(shockwave);
      }

      // Enhanced opponent reaction for potential flinch (30% chance)
      if (Math.random() < 0.3) {
        opponentElement.classList.add('iron-head-flinch-reaction');
      }

      // Create steel energy burst
      for (let i = 0; i < 20; i++) {
        const energyBurst = document.createElement('div');
        energyBurst.className = 'iron-head-energy-burst';
        energyBurst.textContent = '⚡';
        energyBurst.style.position = 'absolute';
        energyBurst.style.left = `${opponentX + Math.cos(i * 18 * Math.PI / 180) * 70}px`;
        energyBurst.style.top = `${opponentY + Math.sin(i * 18 * Math.PI / 180) * 70}px`;
        energyBurst.style.animationDelay = `${i * 25}ms`;
        energyBurst.style.zIndex = '1005';
        battleContainer.appendChild(energyBurst);
        elements.push(energyBurst);
      }
    }, 900);
    elements.push({ type: 'timer', timer: phase3Timer });

    // Phase 4: Reset and cleanup (1500-2000ms)
    const cleanupTimer = setTimeout(() => {
      // Remove player effects
      playerElement.classList.remove('iron-head-user-charge');
      
      // Remove opponent effects
      if (opponentRef.current) {
        opponentRef.current.classList.remove('iron-head-opponent-hit', 'iron-head-flinch-reaction');
      }

      // Clean up all elements
      elements.forEach(element => {
        if (element.type === 'timer') {
          clearTimeout(element.timer);
        } else if (element && element.parentNode) {
          element.remove();
        }
      });

      onComplete();
    }, 2000);

    // Cleanup function
    return () => {
      playerElement.classList.remove('iron-head-user-charge');
      if (opponentRef.current) {
        opponentRef.current.classList.remove('iron-head-opponent-hit', 'iron-head-flinch-reaction');
      }
      
      elements.forEach(element => {
        if (element.type === 'timer') {
          clearTimeout(element.timer);
        } else if (element && element.parentNode) {
          element.remove();
        }
      });
      
      clearTimeout(cleanupTimer);
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
};

export default IronHeadAnimation;
